import { DatePicker, Select, Tag, Button } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import type { FilterDefinition, FilterValues } from "../../types/chartFilters";
import styles from "./ChartFilterBar.module.css";

const { RangePicker } = DatePicker;

export type FilterOnChange = (
  key: string,
  value: string | string[] | [number, number] | undefined
) => void;

interface ChartFilterBarProps {
  filters: FilterDefinition[];
  values: FilterValues;
  onChange: FilterOnChange;
  disabled?: boolean;
}

interface ActiveTag {
  id: string;
  label: string;
  closable: boolean;
  onClose?: () => void;
}

function buildTags(
  filters: FilterDefinition[],
  values: FilterValues,
  onChange: FilterOnChange
): ActiveTag[] {
  const tags: ActiveTag[] = [];

  for (const filter of filters) {
    if (filter.type === "yearRange") {
      const val = values[filter.key] as [number, number] | undefined;
      if (val) {
        tags.push({
          id: filter.key,
          label: `${val[0]} – ${val[1]}`,
          closable: true,
          onClose: () => onChange(filter.key, undefined),
        });
      }
    } else if (filter.type === "multiSelect") {
      const vals = (values[filter.key] as string[]) ?? [];
      vals.forEach((v) => {
        const optLabel = filter.options.find((o) => o.value === v)?.label ?? v;
        tags.push({
          id: `${filter.key}::${v}`,
          label: optLabel,
          closable: true,
          onClose: () =>
            onChange(
              filter.key,
              vals.filter((x) => x !== v)
            ),
        });
      });
    } else if (filter.type === "select") {
      const val = values[filter.key] as string | undefined;
      if (val) {
        const optLabel =
          filter.options.find((o) => o.value === val)?.label ?? val;
        tags.push({
          id: filter.key,
          label: optLabel,
          closable: filter.clearable ?? false,
          onClose: filter.clearable
            ? () => onChange(filter.key, undefined)
            : undefined,
        });
      }
    }
  }

  return tags;
}

const ChartFilterBar = ({ filters, values, onChange, disabled }: ChartFilterBarProps) => {
  const tags = buildTags(filters, values, onChange);

  return (
    <div className={`${styles.container} ${disabled ? styles.disabled : ""}`}>
      {disabled && <div className={styles.disabledOverlay} />}
      <div className={styles.selects}>
        {filters.map((filter) => {
          if (filter.type === "yearRange") {
            const rangeVal = values[filter.key] as [number, number] | undefined;
            return (
              <RangePicker
                key={filter.key}
                picker="year"
                disabled={disabled}
                value={
                  rangeVal
                    ? [dayjs(String(rangeVal[0])), dayjs(String(rangeVal[1]))]
                    : null
                }
                placeholder={[`${filter.minYear}`, `${filter.maxYear}`]}
                minDate={dayjs(String(filter.minYear))}
                maxDate={dayjs(String(filter.maxYear))}
                className={styles.rangePicker}
                onChange={(dates) => {
                  if (dates?.[0] && dates?.[1]) {
                    onChange(filter.key, [dates[0].year(), dates[1].year()]);
                  } else {
                    onChange(filter.key, undefined);
                  }
                }}
              />
            );
          }

          if (filter.type === "multiSelect") {
            const selected = (values[filter.key] as string[]) ?? [];
            const availableOptions = filter.options.filter(
              (o) => !selected.includes(o.value)
            );
            return (
              <Select
                key={filter.key}
                mode="multiple"
                disabled={disabled}
                value={[]}
                options={availableOptions}
                placeholder={filter.placeholder ?? filter.label}
                onChange={(newVals: string[]) => {
                  const merged = selected.concat(newVals);
                  onChange(filter.key, merged.filter((v, i) => merged.indexOf(v) === i));
                }}
                maxTagCount={0}
                className={styles.select}
                allowClear={false}
              />
            );
          }

          if (filter.type === "select") {
            const selectVal = values[filter.key] as string | undefined;
            return (
              <Select
                key={filter.key}
                disabled={disabled}
                value={selectVal ?? null}
                options={filter.options}
                placeholder={filter.label}
                onChange={(v: string) => onChange(filter.key, v)}
                className={styles.select}
              />
            );
          }

          return null;
        })}
      </div>

      {tags.length > 0 && (
        <div className={styles.tags}>
          {tags.map((tag) => (
            <Tag
              key={tag.id}
              closable={disabled ? false : tag.closable}
              onClose={tag.onClose}
              className={styles.tag}
            >
              {tag.label}
            </Tag>
          ))}
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            disabled={disabled}
            className={styles.clearAllBtn}
            onClick={() => {
              for (const filter of filters) {
                if (filter.type === "multiSelect") {
                  onChange(filter.key, []);
                } else if (filter.type === "yearRange") {
                  onChange(filter.key, undefined);
                } else if (filter.type === "select" && (filter.clearable ?? false)) {
                  onChange(filter.key, undefined);
                }
              }
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ChartFilterBar;
